import Iconify from "@/components/ui/Iconify";
import StaticVideoPreview from "@/components/ui/StaticVideoPreview";
import { useTranslations } from "@/lib/static-intl";
import Image from "next/image";
import { useEffect, useRef, useState } from "react";
import { getMediaRecordPath, getMediaRecordUrl } from "@/utils/media";

const PDW_ReviewsMediaSection = ({
  openPlaceMediaSlider,
  placePosts,
  isCssForDialog,
}) => {
  const t = useTranslations("places.place_details_window");
  const [reviewsMedia] = useState([
    ...(placePosts?.map((post) => post?.media)?.flat() || []),
  ]);

  // Reference for scrollable container
  const scrollRef = useRef(null);
  const [isScrollable, setIsScrollable] = useState(false);

  const renderReviewMedia = (media) => {
    const mediaUrl = getMediaRecordUrl(media);
    const imageUrl =
      getMediaRecordUrl(media, { variant: "thumbnail" }) ||
      mediaUrl ||
      "/person.png";
    const posterUrl =
      media?.thumbnailPath || media?.placeholderPath || media?.placeholder
        ? getMediaRecordUrl(media, { variant: "thumbnail" })
        : undefined;

    if (media?.mediaType?.includes("video")) {
      return (
        <StaticVideoPreview
          src={mediaUrl}
          poster={posterUrl}
          className="object-cover w-full h-full"
        />
      );
    } else {
      return (
        <Image
          src={imageUrl}
          alt="sample"
          fill
          sizes="(max-width: 768px)100vw,(max-width: 1200px)50vw, 33vw"
          className="object-contain"
        />
      );
    }
  };

  // Check if the container is scrollable
  useEffect(() => {
    const checkScrollable = () => {
      if (scrollRef.current) {
        const { scrollWidth, clientWidth } = scrollRef.current;
        setIsScrollable(scrollWidth > clientWidth);
      }
    };

    checkScrollable();

    // Optional: Recheck on window resize
    window.addEventListener("resize", checkScrollable);
    return () => {
      window.removeEventListener("resize", checkScrollable);
    };
  }, [reviewsMedia]); // Re-run whenever media changes

  const scrollLeft = () => {
    if (scrollRef?.current) {
      scrollRef?.current?.scrollBy({ left: -300, behavior: "smooth" });
    }
  };

  const scrollRight = () => {
    if (scrollRef?.current) {
      scrollRef?.current?.scrollBy({ left: 300, behavior: "smooth" });
    }
  };

  if (reviewsMedia?.length == 0) return null;

  return (
    <div className="w-full flex flex-col gap-3 py-4 relative">
      <div className="w-fit flex items-center gap-2">
        <span className="font-semibold text-lg">{t("photo/video review")}</span>
      </div>
      {/* Render Scroll Buttons Only When Scrollable */}
      <button
        type="button"
        className="absolute top-[55%] right-2 bg-gray-300 dark:bg-slate text-black/80 hover:text-black dark:text-white/80 dark:hover:text-white border rounded-full z-30"
        onClick={scrollRight}
      >
        <Iconify
          icon={"lsicon:right-filled"}
          className={"!text-inherit !w-5 !h-5"}
        />
      </button>
      <button
        type="button"
        className="absolute top-[55%] left-2 bg-gray-300 dark:bg-slate text-black/80 hover:text-black dark:text-white/80 dark:hover:text-white border rounded-full z-30"
        onClick={scrollLeft}
      >
        <Iconify
          icon={"lsicon:left-filled"}
          className={"!text-inherit !w-5 !h-5"}
        />
      </button>
      <div
        ref={scrollRef}
        className="w-full max-w-full overflow-x-auto rounded-xl overflow-hidden flex gap-1"
      >
        {reviewsMedia.map((_m, index) => (
          <div
            key={index}
            className={`relative ${
              isCssForDialog ? "h-[150px]" : "h-[180px]"
            }  aspect-square min-w-[100px] w-[100px] border dark:border-brown rounded-lg overflow-hidden`}
            onClick={() =>
              openPlaceMediaSlider(
                reviewsMedia.map((media) => getMediaRecordPath(media)),
                index
              )
            }
          >
            {_m?.mediaType?.includes("video") && (
              <div className="absolute top-2 right-2 w-5 h-5 bg-black/10 backdrop-blur rounded-full z-10">
                <Iconify icon={"mynaui:play-solid"} className={"!text-white"} />
              </div>
            )}
            <div className="w-full h-full absolute top-0 left-0 z-20 bg-black/0 hover:bg-black/20 transition ease-in-out duration-300 cursor-pointer" />
            {renderReviewMedia(_m)}
          </div>
        ))}
      </div>
    </div>
  );
};

export default PDW_ReviewsMediaSection;
